import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import API, { formatWIBTime } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Briefcase,
  CheckCircle,
  Warning,
  MagnifyingGlass,
  Target,
  XCircle,
  ArrowDown,
  ArrowUp,
  Lightning,
  Spinner,
  CaretRight,
  Globe,
  Sparkle,
  ArrowClockwise,
  Robot,
} from "@phosphor-icons/react";

function StatCard({ icon: Icon, label, value, color, testId, onClick, highlight }) {
  return (
    <div data-testid={testId} onClick={onClick}
      className={`border p-4 rounded-sm transition-colors ${onClick ? "cursor-pointer hover:border-zinc-400" : ""} ${highlight ? "border-red-300 bg-red-50/30" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</p>
          <p className={`text-2xl font-semibold font-mono tracking-tight ${highlight ? "text-red-700" : "text-zinc-900"}`}>{value}</p>
        </div>
        <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon size={18} weight="regular" style={{ color }} />
        </div>
      </div>
    </div>
  );
}

function DailyBriefingCard({ navigate }) {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const load = async (force = false) => {
    try {
      if (force) setRegenerating(true);
      const { data } = await API.get(`/ai/daily-briefing${force ? "?force=true" : ""}`);
      setBriefing(data);
    } catch (e) {
      console.error("briefing err:", e);
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return null;

  // When admin has disabled Daily Briefing, show a compact banner-only card
  if (briefing?.disabled) {
    return (
      <div data-testid="daily-briefing-disabled" className="border border-zinc-200 bg-zinc-50/50 rounded-sm p-3 flex items-center gap-3">
        <div className="w-7 h-7 rounded-sm bg-zinc-300 flex items-center justify-center flex-shrink-0">
          <Sparkle size={14} weight="fill" className="text-zinc-500" />
        </div>
        <div className="flex-1">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-600">AI Daily Briefing — Dinonaktifkan</h2>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            {briefing.message || "Fitur ini dimatikan oleh admin."}
            {briefing.briefing && " Briefing terakhir masih tersimpan — kunjungi halaman AI Asisten untuk melihatnya."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="daily-briefing" className="border border-violet-200 bg-gradient-to-br from-violet-50/60 to-white rounded-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-sm bg-violet-600 flex items-center justify-center">
            <Sparkle size={14} weight="fill" className="text-white" />
          </div>
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-violet-700">AI Daily Briefing</h2>
            {briefing?.generated_at && (
              <p className="text-[9px] text-zinc-500 font-mono">
                Digenerate {formatWIBTime(briefing.generated_at)}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            data-testid="briefing-regen-btn"
            onClick={() => load(true)}
            disabled={regenerating}
            className="text-[10px] px-2 py-1 border border-violet-200 rounded-sm hover:bg-violet-100 text-violet-700 flex items-center gap-1 disabled:opacity-50"
          >
            <ArrowClockwise size={11} className={regenerating ? "animate-spin" : ""} /> Regenerate
          </button>
          <button
            data-testid="briefing-ask-btn"
            onClick={() => navigate("/ai-assistant")}
            className="text-[10px] px-2 py-1 bg-violet-600 text-white rounded-sm hover:bg-violet-700 flex items-center gap-1"
          >
            <Robot size={11} /> Tanya AI
          </button>
        </div>
      </div>
      <div className="text-sm text-zinc-700 leading-relaxed pl-9">
        {briefing?.error ? (
          <p className="text-xs text-red-600">Gagal generate: {briefing.error}</p>
        ) : briefing?.briefing ? (
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{briefing.briefing}</ReactMarkdown>
          </div>
        ) : (
          <p className="text-xs text-zinc-500">Belum ada briefing untuk hari ini.</p>
        )}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, jobsRes] = await Promise.all([
          API.get("/dashboard/stats"),
          API.get("/dashboard/recent-jobs"),
        ]);
        setStats(statsRes.data);
        setRecentJobs(jobsRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Spinner size={24} className="animate-spin text-zinc-400" /></div>;

  const s = stats || {};
  const now = new Date();
  const greeting = now.getHours() < 12 ? "Selamat pagi" : now.getHours() < 17 ? "Selamat siang" : "Selamat malam";
  const dateStr = now.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const hasActionItems = (s.review_count > 0 || s.unmatched_count > 0 || s.running_jobs > 0);

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">{greeting}, {user?.name || "Admin"}</h1>
        <p className="text-sm text-zinc-500 mt-1">{dateStr}</p>
      </div>

      {/* AI Daily Briefing */}
      <DailyBriefingCard navigate={navigate} />

      {/* Action Items */}
      {hasActionItems && (
        <div data-testid="action-items" className="border border-amber-200 bg-amber-50/40 rounded-sm p-4">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-amber-700 mb-3">Perlu Perhatian</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {s.review_count > 0 && (
              <div data-testid="action-review" onClick={() => navigate("/review")}
                className="flex items-center gap-3 bg-white border border-amber-200 rounded-sm px-3 py-2.5 cursor-pointer hover:border-amber-400 transition-colors">
                <MagnifyingGlass size={18} className="text-amber-600" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-800">{s.review_count} Perlu Review</p>
                  <p className="text-[10px] text-amber-600">Transaksi menunggu verifikasi manual</p>
                </div>
                <CaretRight size={14} className="text-amber-400" />
              </div>
            )}
            {s.unmatched_count > 0 && (
              <div data-testid="action-unresolved" onClick={() => navigate("/unresolved")}
                className="flex items-center gap-3 bg-white border border-red-200 rounded-sm px-3 py-2.5 cursor-pointer hover:border-red-400 transition-colors">
                <XCircle size={18} className="text-red-600" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-red-800">{s.unmatched_count} Tidak Ditemukan</p>
                  <p className="text-[10px] text-red-600">Record tanpa pasangan di sheet</p>
                </div>
                <CaretRight size={14} className="text-red-400" />
              </div>
            )}
            {s.running_jobs > 0 && (
              <div data-testid="action-running" onClick={() => navigate("/jobs")}
                className="flex items-center gap-3 bg-white border border-blue-200 rounded-sm px-3 py-2.5 cursor-pointer hover:border-blue-400 transition-colors">
                <Spinner size={18} className="text-blue-600 animate-spin" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-blue-800">{s.running_jobs} Job Berjalan</p>
                  <p className="text-[10px] text-blue-600">Sedang proses rekonsiliasi</p>
                </div>
                <CaretRight size={14} className="text-blue-400" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Row 1: Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard icon={Briefcase} label="Jobs Hari Ini" value={s.total_jobs_today || 0} color="#09090B" testId="stat-jobs-today" />
        <StatCard icon={CheckCircle} label="Match Rate" value={`${s.success_rate || 0}%`} color="#10B981" testId="stat-success-rate" />
        <StatCard icon={Target} label="Matched" value={(s.matched_count || 0).toLocaleString()} color="#10B981" testId="stat-matched" />
        <StatCard icon={Lightning} label="Via PG" value={(s.pg_transactions || 0).toLocaleString()} color="#8B5CF6" testId="stat-pg" />
        <StatCard icon={Briefcase} label="Total Records" value={(s.total_records || 0).toLocaleString()} color="#71717A" testId="stat-total" />
      </div>

      {/* Stats Row 2: Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={ArrowDown} label="Deposit" value={(s.deposit_count || 0).toLocaleString()} color="#2563EB" testId="stat-deposit" />
        <StatCard icon={ArrowUp} label="Withdraw" value={(s.withdraw_count || 0).toLocaleString()} color="#F59E0B" testId="stat-withdraw" />
        <StatCard icon={MagnifyingGlass} label="Perlu Review" value={s.review_count || 0} color="#D97706" testId="stat-review"
          onClick={() => navigate("/review")} highlight={s.review_count > 0} />
        <StatCard icon={XCircle} label="Tidak Ditemukan" value={s.unmatched_count || 0} color="#DC2626" testId="stat-unmatched"
          onClick={() => navigate("/unresolved")} highlight={s.unmatched_count > 0} />
      </div>

      {/* Per-Site Breakdown */}
      {s.site_breakdown && s.site_breakdown.length > 0 && (
        <div className="border border-zinc-200 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
            <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
              <Globe size={15} /> Ringkasan Per-Site
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-zinc-200">
            {s.site_breakdown.map((site) => (
              <div key={site.site_name} className="px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-zinc-800">{site.site_name}</h3>
                  <span className={`text-xs font-mono px-2 py-0.5 rounded-sm ${site.match_rate >= 99 ? "bg-emerald-50 text-emerald-700" : site.match_rate >= 95 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>
                    {site.match_rate}% match
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase">Jobs</p>
                    <p className="text-sm font-mono font-semibold text-zinc-700">{site.total_jobs}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase">Records</p>
                    <p className="text-sm font-mono font-semibold text-zinc-700">{(site.total_records || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase">Review</p>
                    <p className={`text-sm font-mono font-semibold ${site.review > 0 ? "text-amber-600" : "text-zinc-400"}`}>{site.review}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase">Not Found</p>
                    <p className={`text-sm font-mono font-semibold ${site.unmatched > 0 ? "text-red-600" : "text-zinc-400"}`}>{site.unmatched}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Items — Jobs that need attention */}
      {s.action_items && s.action_items.length > 0 && (
        <div className="border border-zinc-200 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
            <h2 className="text-sm font-semibold text-zinc-900">Job Perlu Tindakan</h2>
          </div>
          <div className="divide-y divide-zinc-100">
            {s.action_items.map((j) => (
              <div key={j.id} onClick={() => navigate(`/jobs/${j.id}`)}
                className="flex items-center gap-4 px-4 py-2.5 hover:bg-zinc-50 cursor-pointer transition-colors">
                <span className="text-xs font-mono text-zinc-500 w-16 shrink-0">{j.id?.slice(0, 8)}</span>
                <span className="text-xs text-zinc-600 w-20 shrink-0">{j.site_name}</span>
                <span className="text-xs font-mono text-zinc-500 flex-1">{j.date_from} ~ {j.date_to}</span>
                <StatusBadge status={j.status} />
                {(j.review_count || 0) > 0 && (
                  <span className="text-[10px] font-mono bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-sm">{j.review_count} review</span>
                )}
                {(j.unmatched_count || 0) > 0 && (
                  <span className="text-[10px] font-mono bg-red-50 text-red-600 px-1.5 py-0.5 rounded-sm">{j.unmatched_count} unresolved</span>
                )}
                <CaretRight size={14} className="text-zinc-300" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Jobs */}
      <div className="border border-zinc-200 rounded-sm">
        <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
          <h2 className="text-sm font-semibold text-zinc-900">Job Terbaru</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">ID</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Site</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Tanggal</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Status</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Records</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Matched</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">PG</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Review</th>
              </tr>
            </thead>
            <tbody>
              {recentJobs.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-zinc-400">Belum ada job.</td></tr>
              ) : recentJobs.map((job) => (
                <tr key={job.id} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors" onClick={() => navigate(`/jobs/${job.id}`)}>
                  <td className="px-4 py-2 text-sm font-mono text-zinc-900">{job.id?.slice(0, 8)}</td>
                  <td className="px-4 py-2 text-xs text-zinc-600">{job.site_name || "-"}</td>
                  <td className="px-4 py-2 text-xs font-mono text-zinc-600">{job.date_from || "-"} ~ {job.date_to || ""}</td>
                  <td className="px-4 py-2"><StatusBadge status={job.status} /></td>
                  <td className="px-4 py-2 text-sm font-mono text-right">{(job.total_records || 0).toLocaleString()}</td>
                  <td className="px-4 py-2 text-sm font-mono text-right text-emerald-600">{job.matched_count || 0}</td>
                  <td className="px-4 py-2 text-sm font-mono text-right text-violet-600">{job.pg_count || 0}</td>
                  <td className="px-4 py-2 text-sm font-mono text-right text-amber-600">{job.review_count || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
